import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Collections } from '../collections.model';
import { BackendResponse, StoreBackend } from './backend';
import { RequestStatus } from './request-status-handler.service';
import { Reference } from './store.model';

export type ReferenceWithId<C extends keyof Collections> = Reference<C> & {
  // The path needs to have a single entry in `ids` for a Document
  readonly path: {
    readonly ids: [string];
  };
};

export class DatastoreDocument<K extends keyof Collections> {
  /**
   * There are two types of documents, those referenced by `id` and those
   * referenced by a `secondary id`. The former have a single `id` in the path,
   * the latter have a query and will need to check `valueChanges` to get the
   * `id`.
   *
   * The latter way will do a network request if the observable has not been
   * subscribed to already (which should be rare).
   */
  private id$: Rx.Observable<string>;

  constructor(
    private ref$: Rx.Observable<Reference<K>>,
    private storeBackend: StoreBackend,
    public status$: Rx.Observable<RequestStatus>,
    private valueChanges$: Rx.Observable<Collections[K]>,
  ) {
    this.id$ = this.ref$.pipe(
      switchMap(ref =>
        ref.path.ids
          ? Rx.of(ref.path.ids[0])
          : this.valueChanges$.pipe(
              map(valueChange => valueChange.id.toString()),
            ),
      ),
    );
  }

  valueChanges(): Rx.Observable<Collections[K]> {
    return this.valueChanges$;
  }

  set(object: Collections[K]): Promise<BackendResponse> {
    return Rx.combineLatest(this.ref$, this.id$)
      .pipe(
        take(1),
        switchMap(([ref, id]) => this.storeBackend.set(ref, id, object)),
      )
      .toPromise();
  }

  update(object: Partial<Collections[K]>): Promise<BackendResponse> {
    return Rx.combineLatest(this.ref$, this.id$)
      .pipe(
        take(1),
        switchMap(([ref, id]) => this.storeBackend.update(ref, id, object)),
      )
      .toPromise();
  }

  remove(): Promise<BackendResponse> {
    return Rx.combineLatest(this.ref$, this.id$)
      .pipe(
        take(1),
        switchMap(([ref, id]) => this.storeBackend.delete(ref, id)),
      )
      .toPromise();
  }
}
